import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import type {
  FocusedPrepareResult,
  FocusedRecord,
  FocusedFinding,
} from "../api";
import ModalBackdrop from "./ui/ModalBackdrop";
import SourcePanel from "./SourcePanel";
import FocusedSourceBackBar from "./FocusedSourceBackBar";
import FocusedRecordList from "./FocusedRecordList";
import type { StoredReduceResult, CitationVerification } from "../api";
import type { LedgerRow } from "../lib/reimbursementSummary";
import {
  recordSetKey,
  matchedSetSignature,
  distillCitedIds,
  filterToCited,
  scopeBannerText,
  scopeBannerIcon,
  focusedTabVisibility,
  type ScopeSurface,
} from "../lib/focusedConversations";
import type {
  CaseRecord,
  CaseVolume,
  ChatMessage,
  FindingAction,
  FindingActionKind,
  ValidationResult,
} from "../types";
import ValidationBadge from "./ui/ValidationBadge";
import ProvenanceView from "./ui/ProvenanceView";
import FindingsSummaryBar from "./ui/FindingsSummaryBar";
import AiAnswer from "./ui/AiAnswer";
import { hasProvenance, REC_CITATION_RE } from "../lib/provenance";
import {
  fingerprintRecordIds,
  storedReflectsCurrent,
  stripDanglingCitation,
} from "../lib/focusedSpanning";
import { buildLedgerPromptBlock } from "../lib/ledgerSummary";
import type { LedgerBucket } from "../lib/financialTotals";
import type { StatusOverride } from "../lib/ledger";
import {
  actionMap,
  effectiveStatus,
  failGateSatisfied,
  withAction,
} from "../lib/findings";
import { useSettings } from "../lib/useSettings";
import { triggerAiUsageRefresh } from "../lib/aiUsageSignal";
import { formatDate, formatMonthYear } from "../lib/formatDate";
import AiDisclaimer from "./ui/AiDisclaimer";
import Tooltip from "./ui/Tooltip";
import CopyButton from "./ui/CopyButton";

// Local-only dismissal flag for the Focused "How this works" explainer.
const FOCUSED_EXPLAINER_KEY = "casethread:focused-explainer-dismissed";

// Prepended to the FIRST user message in a conversation so Claude picks up
// response-style and language preferences without polluting every follow-up
// message. Skipped when the user is already on defaults (concise + English).
function preferencePreamble(
  responseStyle: "concise" | "detailed",
  language: string,
): string | null {
  const lines: string[] = [];
  if (responseStyle === "detailed") {
    lines.push("Answer in depth — include reasoning, caveats, and supporting quotes.");
  } else {
    lines.push("Answer concisely. Prefer short, direct prose over long explanations.");
  }
  if (language && language.toLowerCase() !== "en") {
    lines.push(`Write drafts (letters, complaints) in locale: ${language}.`);
  }
  return lines.length ? `[Preferences: ${lines.join(" ")}]` : null;
}

function prependPreamble(user: string, preamble: string | null): string {
  return preamble ? `${preamble}\n\n${user}` : user;
}

// "case" = entire case (normal interrogate endpoint, volume-aware on
// the backend: current-volume verbatim + closed-volume summaries).
// A volume id string = drill-back into that volume (verbatim records
// from the named volume; other volumes contribute summaries only).
export type InterrogateScope = "case" | string;

interface Props {
  caseId: string;
  recordCount: number;
  // Multi-volume cases surface a scope selector + read-only context
  // indicator. Single-volume cases pass volumes=[] (or just one
  // volume) and this component renders identically to the pre-
  // volumes UI — no selector, no indicator.
  volumes?: CaseVolume[];
  // Controlled scope. Defaults to "case". The parent owns this state
  // so external triggers (sidebar "Ask AI about this volume",
  // Timeline's per-volume summary button) can push scope changes
  // without racing Interrogate's mount cycle.
  scope?: InterrogateScope;
  onScopeChange?: (scope: InterrogateScope) => void;
  // Seed + auto-send a message on mount / when the prop becomes
  // non-null. Used by the SuggestedAction "Help me do this" flow —
  // CaseView sets this, we consume once, then call
  // onInitialMessageConsumed so the parent can clear it (preventing
  // a replay if the user leaves and re-enters the tab).
  initialMessage?: string | null;
  onInitialMessageConsumed?: () => void;
  // Records for resolving inline [Record YYYY-MM-DD] citations in
  // assistant replies into clickable links. Optional so existing
  // call sites that haven't wired this yet still compile.
  records?: CaseRecord[];
  onNavigateToRecord?: (recordId: string) => void;
}

interface QuickAction {
  id: string;
  label: string;
  hint: string;
  prompt: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: "cost_summary",
    label: "💰 Cost summary",
    hint: "Itemised list of costs and financial losses incurred",
    prompt: `Summarise all costs and financial losses I have incurred in this case.
List each item with the date, amount, and description.
Show the total at the end.
Format as a clear itemised list suitable for including in a formal complaint or claim.

Hard rules:
- Only include costs I have personally paid — travel, accommodation, replacements, medical, legal, or other out-of-pocket expenses.
- Do NOT include the principal sum or anything I am still owed.
- Use only figures explicitly stated in the records. Do not infer, estimate, or guess.
- Cite the record date for every item.`,
  },
  {
    id: "cost_schedule",
    label: "📋 Cost schedule",
    hint: "Formal Schedule of Loss for a legal letter or claim",
    prompt: `Generate a formal Schedule of Loss based on the records in this case.

Format it as a professional document with:
- A numbered list of each loss item
- Date incurred (YYYY-MM-DD, taken verbatim from the record)
- Amount (with currency symbol)
- Brief description and basis for the claim
- Total at the bottom
- A footer note that all figures are supported by documentation held on file

This should be suitable for submission to a court, regulator, or insurance company.

Hard rules:
- Only include costs I have personally paid. Do not include the principal sum owed to me.
- Use only figures and dates explicitly stated in the records. Do not infer or round.
- If a required detail is missing, use a bracketed placeholder like [date] or [amount].`,
  },
  {
    id: "summary",
    label: "Summarise case",
    hint: "Chronological overview with dates",
    prompt: `Produce a concise summary of this case.

Structure your answer in markdown:
1. **Overview** — 2–3 sentences on what this case is about and the parties involved.
2. **Chronological timeline** — a bulleted list of every material event in date order. Each bullet must begin with the exact date from the record (YYYY-MM-DD) followed by one sentence. Do not invent or approximate dates — if a record has no precise date, say so explicitly.
3. **Current status** — what the most recent record shows and what (if anything) is outstanding.
4. **Open questions / gaps** — information that is missing from the record.

Cite dates and senders inline. Do not speculate beyond what the records say.`,
  },
  {
    id: "letter",
    label: "Draft letter",
    hint: "Formal letter to the other party or a regulator",
    prompt: `Draft a formal letter on behalf of the case owner, suitable to send to the other party (or to a regulator if that is more appropriate given the record).

Rules:
- Use formal English, neutral tone, no emotive language.
- Reference specific prior correspondence by date (exactly as recorded) and subject.
- State clearly what the recipient is being asked to do and by when.
- Do not invent facts, figures, amounts or dates — use only what is present in the record. If a required detail is missing, insert a bracketed placeholder like [recipient name] so the user can fill it in.

Output as markdown, formatted like a real letter: sender block, date (today), recipient block, subject line, body in numbered paragraphs, sign-off.`,
  },
  {
    id: "complaint",
    label: "Draft complaint",
    hint: "Formal complaint document with timeline",
    prompt: `Draft a formal written complaint based on this case record, suitable for submission to an ombudsman, regulator, or the other party's complaints team.

Required sections (in this order), formatted as markdown with headings:

## Complainant
(use [Your name / address / reference] placeholders if not in the record)

## Respondent
Who the complaint is against, with their contact details if in the record.

## Summary of complaint
A short paragraph describing what went wrong.

## Timeline of events
Every material event in date order. Each line must start with the exact date from the record (YYYY-MM-DD) — no approximations, no fabrications. If a record is undated, say "undated" rather than guessing.

## Impact
The harm or detriment caused, only as described in the record.

## Resolution sought
What the complainant is asking for. If the record does not specify, insert [resolution sought] as a placeholder.

## Supporting evidence
A bulleted list of the records being relied on, each with its date and source type.

Rules: no speculation; no invented figures, names or dates; use only what appears in the record.`,
  },
  {
    id: "facts",
    label: "Key facts",
    hint: "Dates, amounts, names, commitments",
    prompt: `Extract the key facts from this case record. Output as markdown with these sections:

## People & organisations
Bulleted list of every named party, with their role.

## Dates
Bulleted list of every significant date mentioned in the records, each with a one-line description of what happened on that date. Use the exact dates from the record (YYYY-MM-DD). Do not invent or approximate. If a record has no precise date, note it as "undated".

## Amounts & reference numbers
Every monetary figure, account number, case reference, or other identifier mentioned, each with its source.

## Commitments & promises
Statements by any party that commit them to doing something, with the date and sender.

## Contradictions or inconsistencies
Anything in the record that contradicts something else, or any gap that matters.

Rules: only include facts that appear verbatim or near-verbatim in the record. No speculation.`,
  },
];

interface AssistantMeta {
  provider: "anthropic" | "ollama";
  model: string;
  chunked: { summarised_records: number; kept_verbatim: number } | null;
  validation?: ValidationResult | null;
  // Per-finding actions for this reply (local — interrogate isn't cached).
  actions?: FindingAction[];
  // Set on Focused Answer replies: how many retrieved records the answer
  // was scoped to. Drives the "based only on N records" scope banner.
  focusedMatchCount?: number;
  // When the matched set was split into groups, which group of how many this
  // answer was scoped to (1-based index rendered in the banner).
  focusedGroupIndex?: number;
  focusedGroupTotal?: number;
  // Set on a "drill down to cited records" answer: the answer was scoped to
  // the deduped set of records the previous answers cited (`total`), a subset
  // of the full matched set (`matchedTotal`). Drives the honest drill banner.
  focusedDrill?: { total: number; matchedTotal: number };
  // Oldest conversation turns the server dropped to stay within the fit
  // budget (records are never trimmed). Surfaced as a small note.
  trimmedTurns?: number;
}

interface UsageMeter {
  ai_calls: number;
  limit: number | null;
  warn_threshold: number | null;
  reset_at: string;
  byo_key_active: boolean;
  byo_key_set_at: string | null;
  byo_provider: string | null;
  byo_model: string | null;
  allowed: boolean;
}

export default function Interrogate({
  caseId,
  recordCount,
  volumes = [],
  scope = "case",
  onScopeChange,
  initialMessage,
  onInitialMessageConsumed,
  records,
  onNavigateToRecord,
}: Props) {
  const settings = useSettings();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [assistantMeta, setAssistantMeta] = useState<
    Record<number, AssistantMeta>
  >({});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Find & verify" (Focused, the DEFAULT — deterministic retrieval narrows to
  // the matching records, then the AI reasons over ONLY those, nothing
  // summarised away) vs "Draft & summarize" (chat — free-form whole-case
  // drafting/synthesis, the only surface for questions with no searchable
  // topic terms).
  const [mode, setMode] = useState<"chat" | "focused">("focused");
  // Focused-mode working state. `phrase` is both the search seed AND the
  // question put to the AI. `groups` are the editable CONCEPT GROUPS (within
  // a group → OR, across groups → AND); `newConcept` seeds an added group;
  // `preview` is the deterministic retrieval result (matched records + fit)
  // refreshed on every edit.
  const [phrase, setPhrase] = useState("");
  const [groups, setGroups] = useState<string[][]>([]);
  const [newConcept, setNewConcept] = useState("");
  const [preview, setPreview] = useState<FocusedPrepareResult | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [focusedError, setFocusedError] = useState<string | null>(null);
  // Question words held out of retrieval (the INTENT stoplist). Seeded/merged
  // on the phrase path, persisted across deterministic chip-edit re-previews,
  // and reduced by add-back. Shown in a subdued "Not searched" row.
  const [excluded, setExcluded] = useState<
    Array<{ term: string; count: number }>
  >([]);
  // Once the user asks the first question, the matched set is PINNED: every
  // subsequent follow-up answers over exactly these records (by id, no
  // re-retrieval) so the scope is stable and visible. Null until the first
  // ask; "New search" clears it back to the search builder.
  const [pinnedRecords, setPinnedRecords] = useState<FocusedRecord[] | null>(
    null,
  );
  const [pinnedListOpen, setPinnedListOpen] = useState(false);
  const [followUp, setFollowUp] = useState("");
  // When the matched set is too big for one AI pass it's packed into
  // chronological GROUPS; the user interrogates one group at a time.
  // `selectedGroup` is the active group (pre-ask); `pinnedGroup` freezes
  // which group of how many the current conversation is scoped to.
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [pinnedGroup, setPinnedGroup] = useState<{
    index: number;
    total: number;
  } | null>(null);
  // Per-group conversations, session-level, keyed by the group's record-set
  // identity (not index). Switching groups saves the current thread here and
  // loads the target's — the others are retained untouched. Reset only when
  // the underlying matched set changes (see runPrepare's signature check).
  const [groupConvos, setGroupConvos] = useState<
    Record<
      string,
      {
        messages: ChatMessage[];
        meta: Record<number, AssistantMeta>;
        pinned: FocusedRecord[];
      }
    >
  >({});
  // Signature of the last previewed matched set — a change resets all group
  // conversations (item 2 reset boundary).
  const lastMatchSigRef = useRef<string>("");
  // "Drill down to cited records": the distilled subset of the matched set
  // that the answers actually cited, pinned as ITS OWN conversation alongside
  // the per-group ones (both preserved when switching between them). Built on
  // demand from the entry button; session-level; reset like everything else on
  // a new search. `inDrill` = the drill conversation is the active view.
  const [drillRecords, setDrillRecords] = useState<FocusedRecord[] | null>(null);
  const [drillConvo, setDrillConvo] = useState<{
    messages: ChatMessage[];
    meta: Record<number, AssistantMeta>;
  } | null>(null);
  const [inDrill, setInDrill] = useState(false);
  // First-run "How this works" explainer for Focused. Dismissal is local-only
  // (localStorage, no server), following the VolumeBanner pattern.
  const [explainerDismissed, setExplainerDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(FOCUSED_EXPLAINER_KEY) === "1";
    } catch {
      return false;
    }
  });
  const dismissExplainer = useCallback(() => {
    setExplainerDismissed(true);
    try {
      window.localStorage.setItem(FOCUSED_EXPLAINER_KEY, "1");
    } catch {
      /* private mode — it'll reappear on reload, harmless */
    }
  }, []);
  // "Answer across all groups" (map-reduce). One-shot, isolated from the
  // pinned per-group conversation. Per-group findings live in refs (for the
  // reduce input + single-group retry); progress + result live in state.
  const [showCostModal, setShowCostModal] = useState(false);
  const [spanning, setSpanning] = useState<{
    question: string;
    groups: Array<"pending" | "running" | "done" | "failed">;
    running: boolean;
    reply: string | null;
    unverified: string[];
    unverifiedQuotes: number;
    unverifiedDates: number;
    verification: Record<string, CitationVerification>;
    truncated: boolean;
    // Non-null ONLY when this surface is showing a PREVIOUS search's stored
    // answer (fingerprint mismatch / legacy row) — drives the provenance banner
    // and the "previous" tab label. A live current-search run leaves it null.
    provenance: { question: string; count: number; created_at: string } | null;
    error: string | null;
    open: boolean;
  } | null>(null);
  const mapFindingsRef = useRef<Array<FocusedFinding[] | null>>([]);
  const mapFailedRef = useRef<boolean[]>([]);
  const mapUnverifiedQuotesRef = useRef<number[]>([]);
  const mapUnverifiedDatesRef = useRef<number[]>([]);
  const mapTruncatedRef = useRef<boolean[]>([]);
  // Per-group records backing a quote/date-failed finding — unioned at reduce.
  const mapQuoteFailRef = useRef<string[][]>([]);
  const mapDateFailRef = useRef<string[][]>([]);
  // The case's most recently stored cross-group answer (server-persisted), for
  // the restore-on-load affordance.
  const [storedResult, setStoredResult] = useState<StoredReduceResult | null>(null);
  // Identity of the CURRENT search's matched set: fingerprint (for provenance)
  // + record count. Computed from the packed batches the spanning run spans.
  const currentSearch = useMemo(() => {
    const ids = preview
      ? [...new Set(preview.batches.flatMap((b) => b.record_ids))]
      : [];
    return { fingerprint: ids.length ? fingerprintRecordIds(ids) : null, count: ids.length };
  }, [preview]);
  // Verified-quote highlighting: recordId → its finding quotes (from the
  // spanning run), so a citation click can highlight the source span.
  const quotesByRecordRef = useRef<Map<string, string[]>>(new Map());
  // The source side-panel (opened by ANY citation click — never navigate away).
  const [sourcePanel, setSourcePanel] = useState<{
    recordId: string;
    quotes: string[];
  } | null>(null);
  const openSource = useCallback((recordId: string, quotes: string[] = []) => {
    setSourcePanel({ recordId, quotes });
  }, []);
  // Per-message "View sources" toggle (message index → open).
  const [sourcesOpen, setSourcesOpen] = useState<Record<number, boolean>>({});
  const [meter, setMeter] = useState<UsageMeter | null>(null);

  // Round 12, Part 4: the deterministic ledger block, recomputed whenever the
  // facts / category overrides change. Passed into the case-wide interrogate
  // call so the model (esp. the "Summarise case" preset) cites these figures
  // verbatim instead of doing its own — and wrong — arithmetic.
  const ledgerBlockRef = useRef<string>("");
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [{ facts }, { overrides }, { links }] = await Promise.all([
          api.getCaseFacts(caseId),
          api.getAmountCategories(caseId),
          api.getMergeLinks(caseId),
        ]);
        if (!live) return;
        // Feed the SHARED ledger the same inputs the UI uses, so the model's
        // cited net matches the Overview + Summary exactly.
        const bucketOverrides = new Map<string, LedgerBucket>(
          overrides.map((o) => [o.fact_key, o.bucket as LedgerBucket]),
        );
        const statusOverrides = new Map(
          overrides
            .filter((o) => o.status != null || o.amount_cents_override != null)
            .map((o) => [
              o.fact_key,
              {
                status: (o.status ?? null) as StatusOverride["status"],
                amountCentsOverride: o.amount_cents_override ?? null,
              },
            ]),
        );
        const mergeLinks = new Map(
          links.map((l) => [l.pair_key, l.decision as "merge" | "separate"]),
        );
        const recordMeta = new Map(
          (records ?? []).map((r) => [
            r.id,
            { subject: r.subject, record_date: r.record_date },
          ]),
        );
        ledgerBlockRef.current = buildLedgerPromptBlock(
          facts,
          recordMeta,
          { bucketOverrides, statusOverrides, mergeLinks },
          new Date().toISOString(),
        );
      } catch {
        ledgerBlockRef.current = ""; // best-effort; summary still works without it
      }
    })();
    return () => {
      live = false;
    };
  }, [caseId, records]);

  // Per-reply validation with its (local) finding actions applied.
  // Interrogate isn't cached server-side, so actions live in assistantMeta
  // for the session only.
  const effVal = (i: number): ValidationResult | null => {
    const meta = assistantMeta[i];
    return meta?.validation
      ? { ...meta.validation, actions: meta.actions ?? [] }
      : null;
  };
  const setFindingAction = (
    i: number,
    findingId: string,
    kind: FindingActionKind,
  ) => {
    setAssistantMeta((prev) => ({
      ...prev,
      [i]: {
        ...prev[i]!,
        actions: withAction(prev[i]?.actions ?? [], findingId, kind),
      },
    }));
  };
  const msgBlocked = (i: number): boolean => {
    const v = effVal(i);
    return (
      !!v &&
      effectiveStatus(v, actionMap(v)) === "fail" &&
      !failGateSatisfied(v, actionMap(v))
    );
  };
  // One-shot notice rendered above the empty chat after a scope
  // change so the user isn't wondering where their history went.
  // Cleared once the user sends the next message.
  const [scopeChangeNotice, setScopeChangeNotice] = useState<string | null>(
    null,
  );
  const firstScopeRef = useRef(true);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Pre-fill the chat input with a quick-action prompt instead of
  // auto-sending it. The user gets a chance to review and edit
  // before confirming with Send — a single misclick shouldn't cost
  // an AI call. Scroll + focus so they can see what was populated.
  function prefillFromQuickAction(prompt: string) {
    setInput(prompt);
    // Defer to the next tick so React has flushed setInput and the
    // textarea's value is actually the new prompt before we scroll
    // and focus. Without this, the selection/caret math can target
    // the stale value.
    window.setTimeout(() => {
      const ta = inputRef.current;
      if (!ta) return;
      ta.scrollIntoView({ behavior: "smooth", block: "center" });
      ta.focus();
      // Put the cursor at the end so typing extends the prompt
      // rather than replacing it.
      try {
        ta.setSelectionRange(prompt.length, prompt.length);
      } catch {
        /* some browsers throw on detached nodes; ignore */
      }
    }, 0);
  }

  // Clear chat history whenever the scope changes. A new scope is a
  // fresh conversation about different context; carrying messages
  // across would confuse the AI about what records it's grounding in.
  // Skip the notice on initial mount — only render it when the user
  // actively switches scope during a session.
  useEffect(() => {
    if (firstScopeRef.current) {
      firstScopeRef.current = false;
      return;
    }
    const scopeLabel =
      scope === "case"
        ? "Entire case"
        : (() => {
            const v = volumes.find((x) => x.id === scope);
            if (!v) return "this volume";
            const title = v.title?.trim();
            return title
              ? `Volume ${v.volume_number} — ${title}`
              : `Volume ${v.volume_number}`;
          })();
    setScopeChangeNotice(
      `Conversation reset — you switched to ${scopeLabel}.`,
    );
    setMessages([]);
    setAssistantMeta({});
    setError(null);
  }, [scope, volumes]);

  const drillBackVolumeId = scope === "case" ? null : scope;

  const refreshMeter = useCallback(async () => {
    try {
      setMeter(await api.aiUsageMeter());
    } catch {
      /* meter is advisory; ignore load failures */
    }
  }, []);

  useEffect(() => {
    void refreshMeter();
  }, [refreshMeter]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, sending]);

  const send = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed || sending) return;

      // Attach the preferences preamble to the first user turn only. The UI
      // shows the user's literal text; Claude receives it with the preamble
      // prepended so its tone / language preferences propagate.
      const outgoing =
        messages.length === 0
          ? prependPreamble(
              trimmed,
              preferencePreamble(
                settings.interrogate.responseStyle,
                settings.interrogate.language,
              ),
            )
          : trimmed;

      const visibleNext: ChatMessage[] = [
        ...messages,
        { role: "user", content: trimmed },
      ];
      const apiNext: ChatMessage[] = [
        ...messages,
        { role: "user", content: outgoing },
      ];
      setMessages(visibleNext);
      setInput("");
      setScopeChangeNotice(null);
      setSending(true);
      setError(null);
      try {
        // Drill-back hits the volume-scoped endpoint so the AI sees
        // that volume's records verbatim + other closed volumes as
        // summaries. Normal mode hits the regular interrogate path
        // (which is already volume-aware on the backend for
        // multi-volume cases — this one returns current-volume
        // verbatim + closed-volume summaries).
        const res = drillBackVolumeId
          ? await api.interrogateVolume(caseId, drillBackVolumeId, apiNext)
          : await api.interrogate(caseId, apiNext, {
              ledger: ledgerBlockRef.current || undefined,
            });
        const { reply, provider, model, chunked } = res;
        // Only the case-wide interrogate path carries validation today;
        // the volume drill-back endpoint isn't wired for it yet.
        const validation =
          (res as { validation?: ValidationResult | null }).validation ?? null;
        const nextMessages: ChatMessage[] = [
          ...visibleNext,
          { role: "assistant", content: reply },
        ];
        setMessages(nextMessages);
        setAssistantMeta((prev) => ({
          ...prev,
          [nextMessages.length - 1]: {
            provider,
            model,
            chunked,
            validation,
            actions: [],
          },
        }));
        // Refresh so the warning / block banner updates as soon as the
        // user crosses the 90% or 100% threshold, not on next page load.
        void refreshMeter();
        triggerAiUsageRefresh();
      } catch (err) {
        setError((err as Error).message);
        // A 429 with error="usage_limit_reached" should surface as the
        // friendlier blocked banner — the meter refresh picks that up.
        if ((err as Error).message === "usage_limit_reached") {
          void refreshMeter();
          triggerAiUsageRefresh();
        }
      } finally {
        setSending(false);
      }
    },
    [
      caseId,
      messages,
      sending,
      settings.interrogate.responseStyle,
      settings.interrogate.language,
      refreshMeter,
      drillBackVolumeId,
    ],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await send(input);
  }

  // Switching Chat ↔ Focused starts a clean slate — the two modes are
  // different ways of asking, and carrying one's thread into the other
  // would confuse both the reader and (for chat follow-ups) the model.
  const firstModeRef = useRef(true);
  useEffect(() => {
    if (firstModeRef.current) {
      firstModeRef.current = false;
      return;
    }
    setMessages([]);
    setAssistantMeta({});
    setError(null);
    setFocusedError(null);
    setPreview(null);
    setGroups([]);
    setNewConcept("");
    setPhrase("");
    setPinnedRecords(null);
    setPinnedGroup(null);
    setSelectedGroup(0);
    setGroupConvos({});
    setDrillRecords(null);
    setDrillConvo(null);
    setInDrill(false);
    lastMatchSigRef.current = "";
    setPinnedListOpen(false);
    setFollowUp("");
    setSpanning(null);
    setShowCostModal(false);
    setExcluded([]);
  }, [mode]);

  // Deterministic retrieval preview. `body` is {phrase} (expand a new
  // concept group), {groups} (re-preview after chip edits), or both
  // (append an expanded concept). The server returns the authoritative
  // normalized groups. No AI, no usage charge — safe on every edit.
  // Last prepare body, so the error state can offer a Retry.
  const lastPrepareRef = useRef<{ phrase?: string; groups?: string[][] } | null>(
    null,
  );
  const runPrepare = useCallback(
    async (body: { phrase?: string; groups?: string[][] }) => {
      lastPrepareRef.current = body;
      setPreparing(true);
      setFocusedError(null);
      try {
        const res = await api.focusedPrepare(caseId, body);
        setGroups(res.groups);
        setPreview(res);

        // Reset boundary (item 2): if the MATCHED SET changed, every per-group
        // conversation is discarded — a thread keyed to a different record set
        // would be misattributed evidence. Identity is the record-id set, not
        // the group count, so an identical re-preview (e.g. a no-op chip edit)
        // preserves conversations. The drill-down (cited-records) conversation
        // is derived from those answers, so it resets on the same boundary.
        const sig = matchedSetSignature(res.records.map((r) => r.record_id));
        if (sig !== lastMatchSigRef.current) {
          lastMatchSigRef.current = sig;
          setGroupConvos({});
          setMessages([]);
          setAssistantMeta({});
          setPinnedRecords(null);
          setPinnedGroup(null);
          setSelectedGroup(0);
          setDrillRecords(null);
          setDrillConvo(null);
          setInDrill(false);
          setFollowUp("");
        }

        // Excluded question words come only from the phrase path. Merge them
        // into the row (dedupe by term, keep latest count); groups-only
        // re-previews leave the existing row untouched.
        if (typeof body.phrase === "string" && body.phrase.trim()) {
          setExcluded((prev) => {
            const byTerm = new Map(prev.map((e) => [e.term, e]));
            for (const e of res.excluded) byTerm.set(e.term, e);
            return Array.from(byTerm.values());
          });
        }
      } catch (err) {
        // Human-readable, never a bare error token.
        const raw = (err as Error).message;
        const friendly =
          raw === "server_error"
            ? "Something went wrong preparing your search. Please try again."
            : raw === "service_unavailable"
              ? "The service is briefly unavailable. Please try again."
              : raw === "not_found"
                ? "This case could not be found."
                : `Couldn't prepare your search (${raw}). Please try again.`;
        setFocusedError(friendly);
        setPreview(null);
      } finally {
        setPreparing(false);
      }
    },
    [caseId],
  );

  const retryPrepare = useCallback(() => {
    if (lastPrepareRef.current) void runPrepare(lastPrepareRef.current);
  }, [runPrepare]);

  // Add-back: a wrongly-excluded topic word → append it (literal, searched
  // as-is) to concept 1, drop it from the excluded row, re-run the free
  // groups preview.
  const addBackTerm = useCallback(
    (term: string) => {
      const t = term.trim().toLowerCase();
      if (!t) return;
      setExcluded((prev) => prev.filter((e) => e.term !== t));
      const next =
        groups.length === 0
          ? [[t]]
          : groups.map((g, i) => (i === 0 ? [...g, t] : g));
      setGroups(next);
      void runPrepare({ groups: next });
    },
    [groups, runPrepare],
  );

  // "Find records": expand the primary phrase into concept group 1.
  const expandPhrase = useCallback(() => {
    const p = phrase.trim();
    if (!p) return;
    void runPrepare({ phrase: p });
  }, [phrase, runPrepare]);

  const editGroups = useCallback(
    (next: string[][]) => {
      const cleaned = next.filter((g) => g.length > 0);
      setGroups(cleaned);
      if (cleaned.length === 0) {
        setPreview(null);
        return;
      }
      void runPrepare({ groups: cleaned });
    },
    [runPrepare],
  );

  const removeChip = useCallback(
    (groupIdx: number, term: string) =>
      editGroups(
        groups.map((g, i) => (i === groupIdx ? g.filter((t) => t !== term) : g)),
      ),
    [groups, editGroups],
  );
  const addChip = useCallback(
    (groupIdx: number, term: string) => {
      const t = term.trim().toLowerCase();
      if (!t) return;
      const g = groups[groupIdx];
      if (!g || g.includes(t)) return;
      editGroups(groups.map((x, i) => (i === groupIdx ? [...x, t] : x)));
    },
    [groups, editGroups],
  );
  const removeGroup = useCallback(
    (groupIdx: number) => editGroups(groups.filter((_, i) => i !== groupIdx)),
    [groups, editGroups],
  );
  // "+ Add concept (AND)": expand a phrase into a NEW group AND'd with the
  // existing ones. Server appends the expanded group.
  const addConcept = useCallback(() => {
    const p = newConcept.trim();
    if (!p) return;
    setNewConcept("");
    void runPrepare({ groups, phrase: p });
  }, [newConcept, groups, runPrepare]);

  // The records of the active group (from the packed batches), in the order
  // the preview returned them. Single-group cases fall back to the whole set.
  const groupRecords = useCallback(
    (index: number): FocusedRecord[] => {
      if (!preview) return [];
      const batch = preview.batches[index];
      if (!batch) return preview.records;
      const byId = new Map(preview.records.map((r) => [r.record_id, r]));
      return batch.record_ids
        .map((id) => byId.get(id))
        .filter((r): r is FocusedRecord => !!r);
    },
    [preview],
  );

  // Deterministic per-group identity (record set, not index) — the conversation
  // store key.
  const batchKey = useCallback(
    (index: number): string | null => {
      const b = preview?.batches[index];
      return b ? recordSetKey(b.record_ids) : null;
    },
    [preview],
  );

  // Switch the active surface to a GROUP tab. PRESERVES every thread: saves
  // whatever is currently visible (the drill thread if in drill, else the
  // group thread), closes the spanning overlay if open, then loads the target
  // group's thread + repins its records. Handles group→group, drill→group and
  // spanning→group in one atomic set of independent state updates (no stale
  // closure between two transitions).
  const selectGroup = useCallback(
    (index: number) => {
      const spanningOpen = !!(spanning && spanning.open);
      // No-op only when already parked on this group with no overlay/drill.
      if (!inDrill && !spanningOpen && index === selectedGroup) return;
      if (inDrill) {
        setDrillConvo({ messages, meta: assistantMeta });
      } else {
        const oldKey = batchKey(selectedGroup);
        if (oldKey && messages.length > 0) {
          setGroupConvos((prev) => ({
            ...prev,
            [oldKey]: {
              messages,
              meta: assistantMeta,
              pinned: pinnedRecords ?? [],
            },
          }));
        }
      }
      setInDrill(false);
      setSpanning((s) => (s ? { ...s, open: false } : s));
      const newKey = batchKey(index);
      const convo = newKey ? groupConvos[newKey] : undefined;
      const total = Math.max(1, preview?.batches.length ?? 1);
      setSelectedGroup(index);
      setMessages(convo?.messages ?? []);
      setAssistantMeta(convo?.meta ?? {});
      const pinned = convo && convo.pinned.length ? convo.pinned : null;
      setPinnedRecords(pinned);
      setPinnedGroup(pinned ? { index, total } : null);
      setError(null);
      setPinnedListOpen(false);
      setFollowUp("");
    },
    [
      selectedGroup,
      inDrill,
      spanning,
      batchKey,
      messages,
      assistantMeta,
      pinnedRecords,
      groupConvos,
      preview,
    ],
  );

  // ---- Drill down to cited records --------------------------------------
  // The deduped set of record ids the answers actually CITED, across two
  // sources: (a) [rec:ID] tokens in every per-group / drill conversation's
  // assistant text (incl. the live thread), and (b) the STRUCTURED rec_ids of
  // the cross-group reduce result (in-session findings if a spanning run
  // happened, else the persisted 0052 result) — preferred over parsing the
  // reduce answer text. Recomputed as answers accrue.
  const citedIds = useMemo(() => {
    const texts: string[] = [];
    const collect = (msgs: ChatMessage[]) => {
      for (const m of msgs) if (m.role === "assistant" && m.content) texts.push(m.content);
    };
    for (const c of Object.values(groupConvos)) collect(c.messages);
    if (drillConvo) collect(drillConvo.messages);
    collect(messages);
    const reduceRecIds: string[] = [];
    if (spanning) {
      for (const g of mapFindingsRef.current)
        if (g) for (const f of g) for (const id of f.rec_ids) reduceRecIds.push(id);
    } else if (storedResult) {
      for (const g of storedResult.group_findings)
        for (const f of g.findings) for (const id of f.rec_ids) reduceRecIds.push(id);
    }
    return distillCitedIds(texts, reduceRecIds);
  }, [groupConvos, drillConvo, messages, spanning, storedResult]);

  // The distilled records (⊆ the matched set, chronological). Empty until an
  // answer cites something.
  const distilledRecords = useMemo(
    () => (preview ? filterToCited(preview.records, citedIds) : []),
    [preview, citedIds],
  );

  // Enter the drill-down conversation: save the active group thread, pin the
  // distilled set, and load the drill thread (retained across switches). The
  // group threads are untouched.
  const enterDrill = useCallback(() => {
    if (inDrill || distilledRecords.length === 0) return;
    const oldKey = batchKey(selectedGroup);
    if (oldKey && messages.length > 0) {
      setGroupConvos((prev) => ({
        ...prev,
        [oldKey]: { messages, meta: assistantMeta, pinned: pinnedRecords ?? [] },
      }));
    }
    setDrillRecords(distilledRecords);
    setPinnedRecords(distilledRecords);
    setPinnedGroup(null);
    setMessages(drillConvo?.messages ?? []);
    setAssistantMeta(drillConvo?.meta ?? {});
    setInDrill(true);
    setError(null);
    setPinnedListOpen(false);
    setFollowUp("");
  }, [
    inDrill,
    distilledRecords,
    batchKey,
    selectedGroup,
    messages,
    assistantMeta,
    pinnedRecords,
    drillConvo,
  ]);

  // (Leaving the drill-down surface happens by selecting a group tab —
  // selectGroup saves the drill thread and restores the group. There's no
  // separate exit control in the unified tab strip.)

  // Ask a Focused question over the PINNED group. Shared by the first ask
  // (pins the SELECTED group's records) and every follow-up (reuses the same
  // pinned ids — no re-retrieval, so the scope can't drift). Follow-ups send
  // the prior conversation so references resolve; grounding stays on the
  // pinned records (enforced server-side).
  const askFocused = useCallback(
    async (rawQuestion: string) => {
      const question = rawQuestion.trim();
      if (!question || sending) return;

      // Determine the pinned set: freeze the SELECTED group's records on the
      // first ask, reuse them thereafter. Listing/asking is never blocked by
      // size — an over-budget set is split into groups, and we pin one.
      let pinned = pinnedRecords;
      let group = pinnedGroup;
      if (!pinned) {
        if (!preview || preview.records.length === 0) return;
        const total = Math.max(1, preview.batches.length);
        const idx = Math.min(selectedGroup, total - 1);
        pinned = groupRecords(idx);
        if (pinned.length === 0) return;
        group = { index: idx, total };
        setPinnedRecords(pinned);
        setPinnedGroup(group);
        setPinnedListOpen(false);
      }
      const recordIds = pinned.map((r) => r.record_id);
      const collapseCounts: Record<string, number> = {};
      for (const r of pinned)
        if (r.collapse_count > 1) collapseCounts[r.record_id] = r.collapse_count;

      const visibleNext: ChatMessage[] = [
        ...messages,
        { role: "user", content: question },
      ];
      setMessages(visibleNext);
      setFollowUp("");
      setSending(true);
      setError(null);
      setFocusedError(null);
      try {
        const res = await api.focusedAnswer(caseId, {
          recordIds,
          collapseCounts,
          messages: visibleNext,
        });
        const nextMessages: ChatMessage[] = [
          ...visibleNext,
          { role: "assistant", content: res.reply },
        ];
        const nextMeta: Record<number, AssistantMeta> = {
          ...assistantMeta,
          [nextMessages.length - 1]: {
            provider: res.provider,
            model: res.model,
            chunked: null,
            validation: null,
            actions: [],
            focusedMatchCount: res.matched_count,
            focusedGroupIndex:
              !inDrill && group && group.total > 1 ? group.index : undefined,
            focusedGroupTotal:
              !inDrill && group && group.total > 1 ? group.total : undefined,
            focusedDrill: inDrill
              ? {
                  total: pinned!.length,
                  matchedTotal: preview?.matched_count ?? pinned!.length,
                }
              : undefined,
            trimmedTurns: res.trimmed_turns,
          },
        };
        setMessages(nextMessages);
        setAssistantMeta(nextMeta);
        // Persist the ACTIVE conversation so it survives switches. Drill and
        // per-group threads live in separate stores — both retained.
        if (inDrill) {
          setDrillConvo({ messages: nextMessages, meta: nextMeta });
        } else {
          const key = group ? batchKey(group.index) : null;
          if (key)
            setGroupConvos((prev) => ({
              ...prev,
              [key]: { messages: nextMessages, meta: nextMeta, pinned: pinned! },
            }));
        }
        void refreshMeter();
        triggerAiUsageRefresh();
      } catch (err) {
        const msg = (err as Error).message;
        // Drop the optimistic user bubble; surface a friendly message for the
        // fit guard. On the group path a single group fits, so this is rare;
        // on the DRILL path the cited union can exceed budget, so the message
        // is actionable (states the size + the ways forward) rather than a
        // dead-end (audit A5).
        if (msg === "too_large") {
          if (inDrill) {
            const est = (
              err as Error & { details?: Record<string, unknown> }
            ).details?.est_tokens;
            const estStr =
              typeof est === "number"
                ? ` (~${est.toLocaleString()} tokens)`
                : "";
            setError(
              `Your cited set is ${pinned!.length} record${pinned!.length === 1 ? "" : "s"}${estStr}, over the analysis budget. ` +
                `Ways forward: narrow the search terms so answers cite fewer records, interrogate the groups individually, ` +
                `or use “Answer across all groups” for a spanning question.`,
            );
            // DEFERRED (see design report): if this dead-end fires in real
            // use, build the "prepare-over-record_ids" composition — run the
            // distilled ids through /focused/prepare's packing
            // (packChronologicalBatches) to interrogate the cited set in
            // sub-groups, instead of dead-ending here. Not built now by design.
          } else {
            setError(
              "This group plus the conversation exceeds the context budget. Start a new, narrower search.",
            );
          }
        } else {
          setError(msg);
        }
        setMessages(messages);
        if (msg === "usage_limit_reached") {
          void refreshMeter();
          triggerAiUsageRefresh();
        }
      } finally {
        setSending(false);
      }
    },
    [
      caseId,
      pinnedRecords,
      pinnedGroup,
      preview,
      selectedGroup,
      groupRecords,
      messages,
      assistantMeta,
      batchKey,
      sending,
      inDrill,
      refreshMeter,
    ],
  );

  // "New search / Clear": drop the pinned set + conversation and return to
  // the search builder for a fresh set.
  const newSearch = useCallback(() => {
    setPinnedRecords(null);
    setPinnedGroup(null);
    setSelectedGroup(0);
    setPinnedListOpen(false);
    setMessages([]);
    setAssistantMeta({});
    setGroupConvos({});
    setDrillRecords(null);
    setDrillConvo(null);
    setInDrill(false);
    lastMatchSigRef.current = "";
    setError(null);
    setFocusedError(null);
    setPreview(null);
    setGroups([]);
    setNewConcept("");
    setPhrase("");
    setFollowUp("");
    setSpanning(null);
    setShowCostModal(false);
    setExcluded([]);
  }, []);

  // ---- "Answer across all groups" (map-reduce) ---------------------------
  const setGroupStatus = useCallback(
    (i: number, status: "pending" | "running" | "done" | "failed") => {
      setSpanning((s) =>
        s ? { ...s, groups: s.groups.map((g, idx) => (idx === i ? status : g)) } : s,
      );
    },
    [],
  );

  // MAP one group (one AI call) with a single retry. Writes findings/failed
  // into the refs; drives per-group progress in state.
  const mapGroup = useCallback(
    async (index: number, question: string) => {
      if (!preview) return;
      const batch = preview.batches[index];
      if (!batch) return;
      const byId = new Map(preview.records.map((r) => [r.record_id, r]));
      const collapseCounts: Record<string, number> = {};
      for (const id of batch.record_ids) {
        const r = byId.get(id);
        if (r && r.collapse_count > 1) collapseCounts[id] = r.collapse_count;
      }
      setGroupStatus(index, "running");
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const res = await api.focusedMap(caseId, {
            recordIds: batch.record_ids,
            collapseCounts,
            question,
          });
          mapFindingsRef.current[index] = res.findings;
          mapFailedRef.current[index] = false;
          mapUnverifiedQuotesRef.current[index] = res.unverified_quotes.length;
          mapUnverifiedDatesRef.current[index] = res.unverified_dates.length;
          mapQuoteFailRef.current[index] = res.unverified_quote_rec_ids;
          mapDateFailRef.current[index] = res.unverified_date_rec_ids;
          mapTruncatedRef.current[index] = res.truncated === true;
          setGroupStatus(index, "done");
          return;
        } catch {
          if (attempt === 1) {
            mapFailedRef.current[index] = true;
            setGroupStatus(index, "failed");
          }
        }
      }
    },
    [caseId, preview, setGroupStatus],
  );

  // REDUCE the collected findings into one spanning answer.
  const runReduce = useCallback(
    async (question: string) => {
      if (!preview) return;
      setSpanning((s) => (s ? { ...s, running: true, error: null } : s));
      const groupFindings = preview.batches.map((b, i) => ({
        group_index: i,
        from_date: b.from_date,
        to_date: b.to_date,
        findings: mapFindingsRef.current[i] ?? [],
        failed: mapFailedRef.current[i] === true,
      }));
      const unverifiedQuotes = mapUnverifiedQuotesRef.current.reduce(
        (n, v) => n + (v ?? 0),
        0,
      );
      const unverifiedDates = mapUnverifiedDatesRef.current.reduce(
        (n, v) => n + (v ?? 0),
        0,
      );
      // Any group whose findings JSON was cut off — surfaced so the spanning
      // answer isn't shown as complete even when the reduce pass fits.
      const anyMapTruncated = mapTruncatedRef.current.some((t) => t === true);
      // Union the per-group records backing a quote/date-failed finding — the
      // reduce pass rolls these into per-citation gate results for the answer.
      const quoteFailRecIds = [
        ...new Set(mapQuoteFailRef.current.flat().filter(Boolean)),
      ];
      const dateFailRecIds = [
        ...new Set(mapDateFailRef.current.flat().filter(Boolean)),
      ];
      // Build recordId → quotes for verified-quote highlighting in the panel.
      const qbr = new Map<string, string[]>();
      for (const g of groupFindings)
        for (const f of g.findings)
          for (const id of f.rec_ids) {
            const arr = qbr.get(id) ?? [];
            if (f.short_quote) arr.push(f.short_quote);
            qbr.set(id, arr);
          }
      quotesByRecordRef.current = qbr;
      try {
        const red = await api.focusedReduce(caseId, {
          question,
          groupFindings,
          unverifiedQuotes,
          unverifiedDates,
          quoteFailRecIds,
          dateFailRecIds,
          mapTruncated: anyMapTruncated,
          fingerprint: currentSearch.fingerprint ?? undefined,
          matchedCount: currentSearch.count,
        });
        setSpanning((s) =>
          s
            ? {
                ...s,
                running: false,
                reply: red.reply,
                unverified: red.unverified_citations,
                unverifiedQuotes: red.unverified_quotes,
                unverifiedDates: red.unverified_dates,
                verification: red.verification ?? {},
                truncated: red.truncated === true,
                // A fresh run is always current-scope — never a previous answer.
                provenance: null,
              }
            : s,
        );
      } catch (err) {
        setSpanning((s) =>
          s ? { ...s, running: false, error: (err as Error).message } : s,
        );
      }
      void refreshMeter();
      triggerAiUsageRefresh();
    },
    [caseId, preview, refreshMeter, currentSearch],
  );

  const runSpanning = useCallback(async () => {
    const question = phrase.trim();
    if (!preview || preview.batches.length === 0 || !question) return;
    setShowCostModal(false);
    const n = preview.batches.length;
    mapFindingsRef.current = new Array(n).fill(null);
    mapFailedRef.current = new Array(n).fill(false);
    mapUnverifiedQuotesRef.current = new Array(n).fill(0);
    mapUnverifiedDatesRef.current = new Array(n).fill(0);
    mapQuoteFailRef.current = new Array(n).fill(null).map(() => []);
    mapDateFailRef.current = new Array(n).fill(null).map(() => []);
    mapTruncatedRef.current = new Array(n).fill(false);
    setSpanning({
      question,
      groups: new Array(n).fill("pending"),
      running: true,
      reply: null,
      unverified: [],
      unverifiedQuotes: 0,
      unverifiedDates: 0,
      verification: {},
      truncated: false,
      provenance: null,
      error: null,
      open: true,
    });
    // MAP with a concurrency cap of 3 (avoids 429 bursts; per-group progress).
    const CAP = 3;
    let next = 0;
    const worker = async () => {
      while (next < n) {
        const i = next++;
        await mapGroup(i, question);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CAP, n) }, () => worker()),
    );
    await runReduce(question);
  }, [phrase, preview, mapGroup, runReduce]);

  // Retry a single failed group, then re-reduce (others aren't re-run).
  const retryGroup = useCallback(
    async (index: number) => {
      if (!spanning) return;
      await mapGroup(index, spanning.question);
      await runReduce(spanning.question);
    },
    [spanning, mapGroup, runReduce],
  );

  // Load the case's most recent stored cross-group answer (advisory) so the
  // restore affordance survives reloads.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { result } = await api.focusedResult(caseId);
        if (live) setStoredResult(result);
      } catch {
        /* advisory — ignore */
      }
    })();
    return () => {
      live = false;
    };
  }, [caseId]);

  // Restore the server-persisted answer into the spanning view (no AI re-run).
  const restoreStored = useCallback(() => {
    if (!storedResult) return;
    const qbr = new Map<string, string[]>();
    for (const g of storedResult.group_findings)
      for (const f of g.findings)
        for (const id of f.rec_ids) {
          const a = qbr.get(id) ?? [];
          if (f.short_quote) a.push(f.short_quote);
          qbr.set(id, a);
        }
    quotesByRecordRef.current = qbr;
    // Provenance: only current-scope when the stored matched-set fingerprint
    // equals the current search's. Legacy rows (no fingerprint) and different
    // sets are PREVIOUS answers — bannered, never shown as current.
    const reflectsCurrent = storedReflectsCurrent(
      storedResult.fingerprint,
      currentSearch.fingerprint,
    );
    // Defensive: a pre-fix stored answer may end mid-citation — strip + flag.
    const { text: reply, stripped } = stripDanglingCitation(storedResult.answer);
    setSpanning({
      question: storedResult.question,
      groups: storedResult.groups.map((g) => (g.failed ? "failed" : "done")),
      running: false,
      reply,
      unverified: storedResult.unverified.citations,
      unverifiedQuotes: storedResult.unverified.quotes,
      unverifiedDates: storedResult.unverified.dates ?? 0,
      verification: storedResult.unverified.verification ?? {},
      truncated: storedResult.truncated === true || stripped,
      provenance: reflectsCurrent
        ? null
        : {
            question: storedResult.question,
            count: storedResult.matched_count ?? 0,
            created_at: storedResult.created_at,
          },
      error: null,
      open: true,
    });
  }, [storedResult, currentSearch]);

  // Consume initialMessage once: seed the textarea so the user sees
  // what was asked, fire send(), then signal the parent to clear it.
  // A local consumed-ref guards against double-send during React
  // StrictMode's double-invoke effect cycle in development.
  const consumedInitialRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialMessage) return;
    if (consumedInitialRef.current === initialMessage) return;
    consumedInitialRef.current = initialMessage;
    setInput(initialMessage);
    void send(initialMessage);
    onInitialMessageConsumed?.();
    // send is intentionally omitted from deps — we only want to react
    // to a new initialMessage arriving, not to send's closure updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  const blocked =
    !!meter && !meter.allowed && !meter.byo_key_active;
  const warnOnly =
    !!meter &&
    !meter.byo_key_active &&
    meter.allowed &&
    meter.warn_threshold !== null &&
    meter.ai_calls >= meter.warn_threshold;
  const disabled = sending || recordCount === 0 || blocked;

  return (
    <div className="interrogate">
      {recordCount === 0 && (
        <div className="banner-warning">
          This case has no records yet. Add some before interrogating.
        </div>
      )}

      {blocked && (
        <div className="banner-warning banner-danger">
          You've used all your AI calls this month. Upgrade your plan or
          add your own API key in Settings. Your allowance resets on{" "}
          {formatResetDay(meter!.reset_at)}.
        </div>
      )}

      {warnOnly && (
        <div className="banner-warning">
          You've used {meter!.ai_calls} of your {meter!.limit} monthly AI
          requests. Usage resets on {formatResetDay(meter!.reset_at)}.
        </div>
      )}

      <div className="interrogate-mode-toggle" role="tablist" aria-label="Ask mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "focused"}
          className={`interrogate-mode-btn${mode === "focused" ? " is-active" : ""}`}
          onClick={() => setMode("focused")}
        >
          🎯 Find &amp; verify
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "chat"}
          className={`interrogate-mode-btn${mode === "chat" ? " is-active" : ""}`}
          onClick={() => setMode("chat")}
        >
          Draft &amp; summarize
        </button>
      </div>

      {mode === "chat" && (
        <>
          <ScopeSelector
            volumes={volumes}
            scope={scope}
            onScopeChange={(next) => onScopeChange?.(next)}
          />

          <VolumeContextIndicator
            volumes={volumes}
            drillBackVolumeId={drillBackVolumeId}
          />

          {messages.length === 0 && (
            <div className="chat-empty-purpose" role="note">
              Ask for drafts, arguments, or a written summary across the whole
              case — e.g. a position statement or where things stand. On large
              cases, older records are condensed to fit, so don’t use this for
              timelines or “who said what, when” — the <strong>Timeline</strong>{" "}
              tab is complete and chronological, and{" "}
              <strong>Find &amp; verify</strong> gives precise answers with
              verifiable citations.
            </div>
          )}

          {messages.length === 0 && volumes.length > 1 && (
            <ScopeSystemMessage scope={scope} volumes={volumes} />
          )}

          <div className="quick-actions">
            {QUICK_ACTIONS.map((action) => (
              <Tooltip
                key={action.id}
                title={`${action.hint} — pre-fills the chat; you still have to press Send.`}
              >
                <button
                  type="button"
                  className="quick-action"
                  onClick={() => prefillFromQuickAction(action.prompt)}
                  disabled={disabled}
                >
                  <span className="quick-action-label">{action.label}</span>
                  <span className="quick-action-hint">{action.hint}</span>
                </button>
              </Tooltip>
            ))}
          </div>
        </>
      )}

      {mode === "focused" && (
        <HowThisWorks
          dismissed={explainerDismissed}
          onDismiss={dismissExplainer}
          onReopen={() => setExplainerDismissed(false)}
        />
      )}

      {/* The search builder: shown until the FIRST ask in ANY group (the
          search is then locked; each group shows its own thread or a
          "Get answer"). */}
      {mode === "focused" &&
        !(
          messages.length > 0 ||
          Object.values(groupConvos).some((c) => c.messages.length > 0)
        ) && (
        <FocusedPanel
          phrase={phrase}
          setPhrase={setPhrase}
          groups={groups}
          newConcept={newConcept}
          setNewConcept={setNewConcept}
          preparing={preparing}
          focusedError={focusedError}
          disabled={disabled}
          sending={sending}
          onExpand={expandPhrase}
          onAddConcept={addConcept}
          onAddChip={addChip}
          onRemoveChip={removeChip}
          onRemoveGroup={removeGroup}
          excluded={excluded}
          onAddBack={addBackTerm}
          onRetry={retryPrepare}
          message={preview?.message ?? null}
          chipCounts={preview?.chip_counts ?? {}}
        />
      )}

      {/* The unified funnel: persistent question header (1), funnel line (3),
          the conversation tab strip (2), the active surface's scope banner, a
          record-list toggle, and ask-actions (4). One re-presentation of the
          existing per-group/drill/spanning state — the active tab is the scope
          indicator; switching preserves every thread. */}
      {mode === "focused" &&
        preview &&
        (() => {
          const total = Math.max(1, preview.batches.length);
          const activeIndex = Math.min(selectedGroup, total - 1);
          const spanningOpen = !!(spanning && spanning.open);
          // A stored answer from a DIFFERENT search (or a legacy row) is a
          // "previous" answer — drives the tab label + provenance banner. A live
          // run carries its own provenance flag; before the tab is opened we
          // compare the stored fingerprint to the current search.
          const spanningIsPrevious = spanning
            ? !!spanning.provenance
            : !!storedResult &&
              !storedReflectsCurrent(
                storedResult.fingerprint,
                currentSearch.fingerprint,
              );
          const activeKind: "group" | "drill" | "spanning" = spanningOpen
            ? "spanning"
            : inDrill
              ? "drill"
              : "group";
          // Which groups already hold a conversation (an answer) — for the tab
          // dot. While in drill/spanning the live `messages` is NOT the group
          // thread, so read the stored group thread (never misattribute it).
          const convoGroups = new Set<number>();
          preview.batches.forEach((_, i) => {
            const msgs =
              activeKind === "group" && i === activeIndex
                ? messages
                : groupConvos[recordSetKey(preview.batches[i]!.record_ids)]
                    ?.messages ?? [];
            if (msgs.some((m) => m.role === "assistant")) convoGroups.add(i);
          });
          const activeThreadHasAnswer =
            activeKind !== "spanning" &&
            messages.some((m) => m.role === "assistant");
          const activeRecords =
            activeKind === "spanning"
              ? preview.records
              : inDrill
                ? drillRecords ?? []
                : groupRecords(activeIndex);
          // The single scope banner surface — the ACTIVE surface's own claim,
          // shown once it has produced a result.
          const bannerSurface: ScopeSurface | null =
            activeKind === "spanning"
              ? spanning?.reply
                ? { kind: "spanning" }
                : null
              : activeThreadHasAnswer
                ? inDrill
                  ? {
                      kind: "drill",
                      count: activeRecords.length,
                      matchedTotal: preview.matched_count,
                    }
                  : {
                      kind: "group",
                      count: activeRecords.length,
                      index: activeIndex,
                      total,
                    }
                : null;
          return (
            <>
              <FocusedQuestionHeader
                phrase={phrase}
                disabled={sending}
                onEdit={() => {
                  const hasConvo =
                    messages.length > 0 ||
                    Object.values(groupConvos).some(
                      (c) => c.messages.length > 0,
                    ) ||
                    !!drillConvo ||
                    !!spanning;
                  if (
                    hasConvo &&
                    !window.confirm(
                      "Editing your question starts a new search and clears the current answers and follow-ups. Continue?",
                    )
                  )
                    return;
                  const saved = phrase;
                  newSearch();
                  setPhrase(saved);
                }}
              />

              <FocusedFunnel
                matched={preview.matched_count}
                raw={preview.raw_count}
                groups={total}
                cited={distilledRecords.length}
              />

              {preview.broad && (
                <div
                  className="focused-fit-note focused-fit-warning"
                  role="note"
                >
                  <span aria-hidden>⚠️</span> This matched most of your records —
                  the search may be too broad; edit the question to narrow.
                </div>
              )}

              <ConversationTabs
                batches={preview.batches}
                convoGroups={convoGroups}
                activeKind={activeKind}
                activeGroupIndex={activeIndex}
                hasSpanning={!!(spanning || storedResult)}
                spanningIsPrevious={spanningIsPrevious}
                citedCount={distilledRecords.length}
                hasDrillConvo={!!drillConvo && drillConvo.messages.length > 0}
                onSelectGroup={(i) => selectGroup(i)}
                onSelectSpanning={() => {
                  if (spanning)
                    setSpanning((s) => (s ? { ...s, open: true } : s));
                  else restoreStored();
                }}
                onSelectDrill={() => {
                  if (spanningOpen)
                    setSpanning((s) => (s ? { ...s, open: false } : s));
                  enterDrill();
                }}
                disabled={disabled}
                sending={sending}
              />

              {bannerSurface && <FocusedScopeBanner surface={bannerSurface} />}

              <div className="focused-records-toggle">
                <button
                  type="button"
                  className="focused-pinbar-toggle"
                  onClick={() => setPinnedListOpen((v) => !v)}
                  aria-expanded={pinnedListOpen}
                >
                  {pinnedListOpen
                    ? "Hide records"
                    : `View records (${activeRecords.length})`}
                </button>
                {(pinnedRecords || inDrill) && !spanningOpen && (
                  <Tooltip title="Edit the question and run a new search">
                    <button
                      type="button"
                      className="focused-pinbar-new"
                      onClick={newSearch}
                      disabled={sending}
                    >
                      New search
                    </button>
                  </Tooltip>
                )}
              </div>
              {pinnedListOpen && (
                <FocusedRecordList
                  records={activeRecords}
                  // Open the source IN PLACE (the same drawer citations use) —
                  // never navigate to the Records tab, which would unmount the
                  // whole Focused session (matched set, groups, conversation).
                  // The drawer keeps everything mounted behind a scrim; its
                  // "← Back to results" bar returns to this exact list.
                  onNavigateToRecord={(id) =>
                    openSource(id, quotesByRecordRef.current.get(id) ?? [])
                  }
                />
              )}

              {activeKind !== "spanning" && (
                <FocusedActions
                  activeKind={activeKind}
                  activeGroupPinned={!!pinnedRecords}
                  groupIndex={activeIndex}
                  groupTotal={total}
                  groupCount={activeRecords.length}
                  canAsk={!!phrase.trim()}
                  onAsk={() => askFocused(phrase)}
                  onAnswerAll={() => setShowCostModal(true)}
                  spanningActive={!!spanning}
                  disabled={disabled}
                  sending={sending}
                />
              )}
            </>
          );
        })()}

      {/* Spanning surface body (map-reduce): shown when its tab is active,
          mutually exclusive with the per-group/drill thread below. Its scope
          banner now renders under the tab strip (above). */}
      {mode === "focused" && spanning && spanning.open && (
        <SpanningAnswer
          state={spanning}
          batches={preview?.batches ?? []}
          records={records ?? []}
          rerunCallCount={(preview?.batches.length ?? 0) + 1}
          onRerun={() => setShowCostModal(true)}
          onRetryGroup={retryGroup}
          onClose={() =>
            setSpanning((s) => (s ? { ...s, open: false } : s))
          }
          onOpenSource={(id) =>
            openSource(id, quotesByRecordRef.current.get(id) ?? [])
          }
        />
      )}

      {mode === "focused" && showCostModal && preview && (
        <SpanningCostModal
          groupCount={preview.batches.length}
          recordCount={preview.matched_count}
          onConfirm={() => void runSpanning()}
          onCancel={() => setShowCostModal(false)}
        />
      )}

      {/* The active per-group/drill thread. Hidden when the spanning tab is
          active (that surface renders above, mutually exclusive). */}
      {!(mode === "focused" && spanning && spanning.open) && (
      <div className="chat-messages" ref={scrollRef}>
        {scopeChangeNotice && (
          <div className="chat-scope-notice" role="status">
            {scopeChangeNotice}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`chat-message chat-${m.role}`}>
            <div className="chat-role">
              <span>{m.role === "user" ? "You" : "CaseThread"}</span>
              {m.role === "assistant" && m.content && (
                <ValidationBadge
                  validation={effVal(i)}
                  onAction={(id, k) => setFindingAction(i, id, k)}
                  compact
                />
              )}
              {m.role === "assistant" && m.content && !msgBlocked(i) && (
                <CopyButton
                  variant="ghost"
                  text={m.content}
                  title="Copy this reply to clipboard"
                />
              )}
              {m.role === "assistant" && m.content && hasProvenance(m.content) && (
                <button
                  type="button"
                  className="chat-sources-toggle"
                  onClick={() =>
                    setSourcesOpen((prev) => ({ ...prev, [i]: !prev[i] }))
                  }
                  aria-pressed={!!sourcesOpen[i]}
                >
                  {sourcesOpen[i] ? "Hide sources" : "View sources"}
                </button>
              )}
            </div>
            {m.role === "assistant" && sourcesOpen[i] ? (
              <ProvenanceView
                text={m.content}
                records={records ?? []}
                onNavigateToRecord={openSource}
              />
            ) : m.role === "assistant" ? (
              <div className="chat-content markdown">
                {assistantMeta[i]?.chunked && (
                  <div className="interrogate-chunked-warning">
                    <span aria-hidden>⚠️</span>
                    Case is large — {assistantMeta[i]!.chunked!.summarised_records} older records were summarised to fit context;{" "}
                    {assistantMeta[i]!.chunked!.kept_verbatim} (pinned + most recent) kept verbatim. For full detail on older items, ask about specific date ranges.
                    {i > 0 && messages[i - 1]?.role === "user" && (
                      <div className="interrogate-chunked-cta">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setPhrase(messages[i - 1]!.content);
                            setMode("focused");
                          }}
                        >
                          🎯 Switch to Find &amp; verify
                        </button>{" "}
                        for a precise, fully-cited answer over just the matching
                        records (you’ll confirm the search terms).
                      </div>
                    )}
                  </div>
                )}
                <AiAnswer
                  text={m.content}
                  records={records ?? []}
                  onNavigateToRecord={openSource}
                />
                {!!assistantMeta[i]?.trimmedTurns && (
                  <div className="focused-trim-note" role="note">
                    <span aria-hidden>ℹ️</span> The {assistantMeta[i]!.trimmedTurns}{" "}
                    oldest question{assistantMeta[i]!.trimmedTurns === 1 ? "" : "s"}{" "}
                    in this conversation {assistantMeta[i]!.trimmedTurns === 1 ? "was" : "were"}{" "}
                    dropped from context to stay within the limit — the pinned
                    records were kept in full. Start a new search if you need a
                    fresh thread.
                  </div>
                )}
                {assistantMeta[i] && (
                  <div
                    className={`chat-model-badge model-${
                      assistantMeta[i]!.provider === "ollama"
                        ? "local"
                        : "cloud"
                    }`}
                  >
                    <span aria-hidden>
                      {assistantMeta[i]!.provider === "ollama" ? "🏠" : "☁️"}
                    </span>
                    <strong>{assistantMeta[i]!.model}</strong>
                    <span className="chat-model-kind">
                      {assistantMeta[i]!.provider === "ollama"
                        ? "Local"
                        : "Cloud"}
                    </span>
                    <span className="chat-model-note">
                      {assistantMeta[i]!.provider === "ollama"
                        ? "Processed locally — nothing left your device"
                        : "Processed via Anthropic API"}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="chat-content">{m.content}</div>
            )}
            {m.role === "assistant" && effVal(i) && (
              <FindingsSummaryBar
                validation={effVal(i)!}
                onAction={(id, k) => setFindingAction(i, id, k)}
                inlineMappable={false}
              />
            )}
          </div>
        ))}
        {sending && (
          <div
            className="chat-message chat-assistant chat-pending"
            role="status"
            aria-live="polite"
          >
            <div className="chat-role">CaseThread</div>
            <div className="chat-content">Thinking…</div>
          </div>
        )}
      </div>
      )}

      {error && <div className="form-error">{error}</div>}

      {mode === "chat" && (
        <>
          {messages.length === 0 && !scopeChangeNotice && (
            <p className="chat-input-hint">
              Pick a quick action above, or ask your own question below.
            </p>
          )}

          <form className="chat-input" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  (e.currentTarget.form as HTMLFormElement).requestSubmit();
                }
              }}
              placeholder={
                blocked
                  ? "Monthly AI limit reached — waiting for reset."
                  : drillBackVolumeId
                    ? "Ask about this volume… (Cmd/Ctrl+Enter to send)"
                    : "Ask about this case… (Cmd/Ctrl+Enter to send)"
              }
              rows={3}
              disabled={sending || blocked}
            />
            <Tooltip title="Send your question to the AI">
              <button
                type="submit"
                className="btn-primary"
                disabled={disabled || !input.trim()}
              >
                Send
              </button>
            </Tooltip>
          </form>
        </>
      )}

      {mode === "focused" && pinnedRecords && !(spanning && spanning.open) && (
        <form
          className="chat-input focused-followup"
          onSubmit={(e) => {
            e.preventDefault();
            void askFocused(followUp);
          }}
        >
          <textarea
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                (e.currentTarget.form as HTMLFormElement).requestSubmit();
              }
            }}
            placeholder={
              blocked
                ? "Monthly AI limit reached — waiting for reset."
                : `Ask another question about these ${pinnedRecords.length} records… (Cmd/Ctrl+Enter)`
            }
            rows={2}
            disabled={sending || blocked}
          />
          <Tooltip title="Ask a follow-up over the same pinned records">
            <button
              type="submit"
              className="btn-primary"
              disabled={disabled || !followUp.trim()}
            >
              Ask
            </button>
          </Tooltip>
        </form>
      )}

      {/* Source side-panel — opened by ANY citation click (the answer stays on
          screen; this is a drawer, not navigation). Reuses the Financials
          SourcePanel incl. verbatim-quote highlighting. */}
      {sourcePanel && (
        <>
          <div className="rs-panel-scrim" onClick={() => setSourcePanel(null)} />
          <div className="rs-panel rs-panel-drawer">
            <FocusedSourceBackBar onBack={() => setSourcePanel(null)} />
            <SourcePanel
              caseId={caseId}
              records={records ?? []}
              row={
                {
                  source: { recordId: sourcePanel.recordId, attachmentId: null },
                  dateRaw: "",
                  party: null,
                } as unknown as LedgerRow
              }
              quotes={sourcePanel.quotes}
              onClose={() => setSourcePanel(null)}
              onOpenFullRecord={() => {
                onNavigateToRecord?.(sourcePanel.recordId);
                setSourcePanel(null);
              }}
            />
          </div>
        </>
      )}

      <AiDisclaimer />
    </div>
  );
}

function formatResetDay(iso: string): string {
  return formatDate(iso, "long") || iso;
}

// ---------------------------------------------------------------------------
// Focused panel, unified. The panel presents one staged funnel: question →
// terms → match → groups → answers → drill-down. These small presentational
// components replace the old monolithic MatchedSetPanel: a persistent question
// header (1), a funnel line tying the counts to their nouns (3), one
// conversation tab strip for every interrogation surface (2), a single
// active-surface scope banner, and ask-actions that state what they do (4).
// All re-present existing state — no retrieval/packing/answer changes.
// ---------------------------------------------------------------------------

// (1) The user's original question, pinned above the panel through the whole
// conversation so the object of every "ask" is unambiguous. Truncated with
// expand-on-click; the edit affordance is today's new-search path (the caller
// confirms the reset when conversations exist).
function FocusedQuestionHeader({
  phrase,
  onEdit,
  disabled,
}: {
  phrase: string;
  onEdit: () => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = phrase.trim();
  if (!trimmed) return null;
  const long = trimmed.length > 90;
  const shown = expanded || !long ? trimmed : `${trimmed.slice(0, 90).trimEnd()}…`;
  return (
    <div className="focused-question-header">
      <span className="focused-question-label">Your question:</span>{" "}
      <button
        type="button"
        className="focused-question-text"
        onClick={() => long && setExpanded((v) => !v)}
        title={long ? (expanded ? "Show less" : "Show full question") : undefined}
      >
        “{shown}”
      </button>
      <Tooltip title="Edit the question and run a new search">
        <button
          type="button"
          className="focused-question-edit"
          onClick={onEdit}
          disabled={disabled}
        >
          Edit
        </button>
      </Tooltip>
    </div>
  );
}

// (3) The funnel line: each count adjacent to its noun, tying the stages
// together. The thread-collapse note stays attached to the matched segment;
// the "cited" segment appears once nonzero. Derived from the same preview +
// citedIds as the tabs — single source, no independent computation.
function FocusedFunnel({
  matched,
  raw,
  groups,
  cited,
}: {
  matched: number;
  raw: number;
  groups: number;
  cited: number;
}) {
  const collapseNote =
    raw > matched ? ` (${raw} → ${matched} after collapsing threads)` : "";
  return (
    <div className="focused-funnel" role="note">
      <strong>{matched}</strong> record{matched === 1 ? "" : "s"} matched
      {collapseNote}
      {groups > 1 && (
        <>
          {" "}
          → split into <strong>{groups}</strong> groups by date
        </>
      )}
      {cited > 0 && (
        <>
          {" "}
          → <strong>{cited}</strong> cited by answers so far
        </>
      )}
    </div>
  );
}

// (2) The unified conversation tab strip: one row for every interrogation
// surface. Group tabs (with the conversation-presence dot), the spanning
// answer tab (only once a result exists — its run entry point is an action,
// not a free-looking tab), and the cited-records tab (only once the cited set
// is non-empty). The active tab IS the scope indicator; switching preserves
// every thread (Part A/B state, untouched).
function ConversationTabs({
  batches,
  convoGroups,
  activeKind,
  activeGroupIndex,
  hasSpanning,
  spanningIsPrevious,
  citedCount,
  hasDrillConvo,
  onSelectGroup,
  onSelectSpanning,
  onSelectDrill,
  disabled,
  sending,
}: {
  batches: FocusedPrepareResult["batches"];
  convoGroups: Set<number>;
  activeKind: "group" | "drill" | "spanning";
  activeGroupIndex: number;
  hasSpanning: boolean;
  spanningIsPrevious: boolean;
  citedCount: number;
  hasDrillConvo: boolean;
  onSelectGroup: (index: number) => void;
  onSelectSpanning: () => void;
  onSelectDrill: () => void;
  disabled: boolean;
  sending: boolean;
}) {
  const multi = batches.length > 1;
  const vis = focusedTabVisibility({ hasSpanning, citedCount });
  return (
    <div className="focused-group-tabs" role="tablist">
      {batches.map((b, i) => {
        const active = activeKind === "group" && i === activeGroupIndex;
        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={active}
            className={`focused-group-tab${active ? " is-active" : ""}`}
            onClick={() => onSelectGroup(i)}
            disabled={disabled || sending}
            title={`${b.from_date} → ${b.to_date}`}
          >
            <span className="focused-group-tab-label">
              {multi ? `Group ${i + 1}` : "Matched set"}
              {convoGroups.has(i) && (
                <span
                  className="focused-group-tab-dot"
                  title="This group has a conversation"
                  aria-label="has conversation"
                >
                  ●
                </span>
              )}
            </span>
            <span className="focused-group-tab-meta">
              {b.from_date === b.to_date
                ? b.from_date
                : `${b.from_date} → ${b.to_date}`}{" "}
              · {b.count}
            </span>
          </button>
        );
      })}

      {vis.spanning && (
        <button
          type="button"
          role="tab"
          aria-selected={activeKind === "spanning"}
          className={`focused-group-tab${activeKind === "spanning" ? " is-active" : ""}`}
          onClick={onSelectSpanning}
          disabled={disabled || sending}
          title={
            spanningIsPrevious
              ? "A spanning answer from a PREVIOUS search — does not reflect the current search"
              : "The one-shot answer consolidated across all groups"
          }
        >
          <span className="focused-group-tab-label">
            {spanningIsPrevious ? "⏳ Previous spanning answer" : "✨ Spanning answer"}
          </span>
        </button>
      )}

      {vis.cited && (
        <button
          type="button"
          role="tab"
          aria-selected={activeKind === "drill"}
          className={`focused-group-tab${activeKind === "drill" ? " is-active" : ""}`}
          onClick={onSelectDrill}
          disabled={disabled || sending}
          title="The records your answers have cited so far, from all groups — interrogate just these"
        >
          <span className="focused-group-tab-label">
            🎯 Cited records
            {hasDrillConvo && (
              <span
                className="focused-group-tab-dot"
                title="This drill-down has a conversation"
                aria-label="has conversation"
              >
                ●
              </span>
            )}
          </span>
          <span className="focused-group-tab-meta">· {citedCount}</span>
        </button>
      )}
    </div>
  );
}

// The single active-surface scope banner, rendered directly under the strip.
// The wording is the ACTIVE surface's own claim (group / spanning / drill are
// semantically different "unanswered" statements) — never a generic fallback.
function FocusedScopeBanner({ surface }: { surface: ScopeSurface }) {
  return (
    <div className="focused-scope-banner" role="note">
      <span aria-hidden>{scopeBannerIcon(surface)}</span>{" "}
      {scopeBannerText(surface)}
    </div>
  );
}

// (4) Ask-actions that state exactly what they do. The first ask over the
// active group uses the pinned question; "Ask across all groups" shows the
// exact call count. Follow-ups happen in the thread's own input.
function FocusedActions({
  activeKind,
  activeGroupPinned,
  groupIndex,
  groupTotal,
  groupCount,
  canAsk,
  onAsk,
  onAnswerAll,
  spanningActive,
  disabled,
  sending,
}: {
  activeKind: "group" | "drill" | "spanning";
  activeGroupPinned: boolean;
  groupIndex: number;
  groupTotal: number;
  groupCount: number;
  canAsk: boolean;
  onAsk: () => void;
  onAnswerAll: () => void;
  spanningActive: boolean;
  disabled: boolean;
  sending: boolean;
}) {
  const multi = groupTotal > 1;
  const showAsk = activeKind === "group" && !activeGroupPinned;
  // Show the run action whenever there's no LIVE current-search spanning being
  // displayed. A persisted result (possibly from a DIFFERENT search) must NOT
  // hide it — that was the stored-result-masks-fresh-run bug. Keys off the live
  // run only, never the stored result.
  const showAnswerAll = multi && !spanningActive;
  if (!showAsk && !showAnswerAll) return null;
  return (
    <div className="focused-ask-row">
      {showAsk && (
        <Tooltip title="Send your pinned question to the AI over this scope only">
          <button
            type="button"
            className="btn-primary"
            onClick={onAsk}
            disabled={disabled || sending || !canAsk || groupCount === 0}
          >
            {sending
              ? "Answering…"
              : multi
                ? `Ask this question — Group ${groupIndex + 1} (${groupCount} records)`
                : `Ask this question (${groupCount} records)`}
          </button>
        </Tooltip>
      )}
      {showAnswerAll && (
        <Tooltip title="Run the question over every group and consolidate into one cited answer">
          <button
            type="button"
            className="btn-secondary"
            onClick={onAnswerAll}
            disabled={disabled || sending || spanningActive || !canAsk}
          >
            Ask across all groups ({groupTotal + 1} AI calls)
          </button>
        </Tooltip>
      )}
    </div>
  );
}

// (6) The quiet, dismissible first-run explainer: the four Focused steps, one
// short line each. Dismissal is local-only; a small "?" reopens it.
function HowThisWorks({
  dismissed,
  onDismiss,
  onReopen,
}: {
  dismissed: boolean;
  onDismiss: () => void;
  onReopen: () => void;
}) {
  if (dismissed) {
    return (
      <button
        type="button"
        className="focused-howto-reopen"
        onClick={onReopen}
        title="How Find & verify works"
        aria-label="How Find & verify works"
      >
        ?
      </button>
    );
  }
  return (
    <div className="focused-howto" role="note">
      <span className="focused-howto-steps">
        <strong>How this works —</strong> 1. Ask a question → 2. Confirm the
        search terms → 3. Get answers per group or across all → 4. Drill into the
        cited records with follow-ups.
      </span>
      <button
        type="button"
        className="focused-howto-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// Confirmation modal before the map-reduce run. Call count is exact
// (N groups + 1 consolidation); tokens aren't shown (honest — we only know
// the per-group estimates, not the model's output).
function SpanningCostModal({
  groupCount,
  recordCount,
  onConfirm,
  onCancel,
}: {
  groupCount: number;
  recordCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ModalBackdrop onClose={onCancel}>
      <div className="modal-card focused-cost-modal" role="dialog" aria-modal="true">
        <h3>Answer across all {groupCount} groups?</h3>
        <p>
          This runs <strong>{groupCount} answer passes + 1 consolidation</strong>{" "}
          (~{groupCount + 1} AI calls) over {recordCount} records. Each group is
          analysed in full, then the findings are paired across groups into one
          cited answer.
        </p>
        <p className="focused-cost-note">
          This is a one-shot answer — not a follow-up conversation. Per-group
          interrogation stays available and costs one call at a time.
        </p>
        <div className="focused-cost-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={onConfirm}>
            Run {groupCount + 1} AI calls
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// The one-shot cross-group answer: per-group progress, the spanning answer
// (with click-through citations), the unverified-citation flag, and a
// single-group retry for any group whose map pass failed.
function SpanningAnswer({
  state,
  batches,
  records,
  rerunCallCount,
  onRerun,
  onRetryGroup,
  onClose,
  onOpenSource,
}: {
  state: {
    question: string;
    groups: Array<"pending" | "running" | "done" | "failed">;
    running: boolean;
    reply: string | null;
    unverified: string[];
    unverifiedQuotes: number;
    unverifiedDates: number;
    verification: Record<string, CitationVerification>;
    truncated: boolean;
    provenance: { question: string; count: number; created_at: string } | null;
    error: string | null;
  };
  batches: FocusedPrepareResult["batches"];
  records: CaseRecord[];
  rerunCallCount: number;
  onRerun: () => void;
  onRetryGroup: (index: number) => void;
  onClose: () => void;
  onOpenSource: (recordId: string) => void;
}) {
  const anyFailed = state.groups.some((g) => g === "failed");
  const icon = { pending: "•", running: "…", done: "✓", failed: "⚠" } as const;
  // "Show" toggle: emphasize the flagged citation chips and scroll to the first.
  const [showFlagged, setShowFlagged] = useState(false);
  const replyRef = useRef<HTMLDivElement | null>(null);
  // Distinct cited records IN THE ANSWER whose per-record gate rollup has any
  // failure — the exact records the user needs to eyeball. Per-citation, not
  // per-row: a record cited on several rows is one entry here (marked on each).
  const flaggedCitationIds = useMemo(() => {
    if (!state.reply) return [] as string[];
    const ids = new Set<string>();
    for (const m of state.reply.matchAll(REC_CITATION_RE)) {
      const id = m[1]!;
      const v = state.verification[id];
      if (v && (!v.citation_ok || !v.quote_ok || !v.date_ok)) ids.add(id);
    }
    return [...ids];
  }, [state.reply, state.verification]);
  const revealFlagged = () => {
    setShowFlagged(true);
    // Scroll the first flagged chip into view once the emphasis class lands.
    requestAnimationFrame(() => {
      replyRef.current
        ?.querySelector(".cite-ref-flagged")
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  };
  const prov = state.provenance;
  return (
    <div className="spanning-answer">
      <div className="spanning-head">
        <span className="spanning-title">
          {prov ? "⏳ Previous spanning answer" : "🧩 Answer across all groups"}
        </span>
        <span className="spanning-head-actions">
          {/* A fresh run is ALWAYS available here — a stored/previous result
              never blocks it. Opens the cost modal → fans out over the CURRENT
              groups → replaces this result. */}
          <button
            type="button"
            className="focused-pinbar-new"
            onClick={onRerun}
            disabled={state.running}
            title="Run the question over the CURRENT search's groups and replace this answer"
          >
            {prov ? "Re-run for current search" : "Re-run"} ({rerunCallCount} AI
            calls)
          </button>
          <button type="button" className="focused-pinbar-new" onClick={onClose}>
            Close
          </button>
        </span>
      </div>

      {/* Provenance: this stored answer is from a DIFFERENT search (or a legacy
          row). Never render it under the current-scope banner. */}
      {prov && (
        <div className="focused-fit-note focused-fit-warning" role="alert">
          <span aria-hidden>⏳</span> This answer is from a previous search —
          “{prov.question}” ({prov.count} record{prov.count === 1 ? "" : "s"},{" "}
          {new Date(prov.created_at).toLocaleString()}). It does not reflect your
          current search or groups. Re-run above to answer the current search.
        </div>
      )}

      <div className="spanning-groups" role="status">
        {state.groups.map((st, i) => (
          <span key={i} className={`spanning-group spanning-${st}`}>
            <span aria-hidden>{icon[st]}</span> Group {i + 1}
            <span className="spanning-group-meta">
              {batches[i]
                ? batches[i]!.from_date === batches[i]!.to_date
                  ? batches[i]!.from_date
                  : `${batches[i]!.from_date}→${batches[i]!.to_date}`
                : ""}
            </span>
            {st === "failed" && (
              <button
                type="button"
                className="spanning-retry"
                onClick={() => onRetryGroup(i)}
                disabled={state.running}
              >
                Retry
              </button>
            )}
          </span>
        ))}
      </div>

      {anyFailed && !state.running && (
        <div className="focused-fit-note focused-fit-warning" role="note">
          <span aria-hidden>⚠️</span> Some groups failed — the answer below is
          based on the groups that succeeded. Retry the failed group(s) for a
          complete answer.
        </div>
      )}

      {state.running && !state.reply && (
        <div className="spanning-pending" role="status" aria-live="polite">
          Analysing each group, then consolidating…
        </div>
      )}

      {state.error && <div className="form-error">{state.error}</div>}

      {state.reply && (
        <div className="chat-content markdown spanning-reply" ref={replyRef}>
          {/* Truncation is a hard warning: the answer was cut off by a length
              limit, so rows below the last complete entry are silently missing.
              This must never be presented as a complete answer. */}
          {state.truncated && (
            <div className="focused-fit-note focused-fit-warning" role="alert">
              <span aria-hidden>⚠️</span> This answer was cut off by a length
              limit — entries may be missing below the last complete item.
              Re-run with a narrower question or fewer groups for a complete
              answer.
            </div>
          )}
          {/* Actionable per-citation verification: names how many cited records
              need checking and jumps to them. No aggregate warning without a
              path to the citations it counts. Granularity is per-citation
              (per-record), matching what the gates verify. */}
          {flaggedCitationIds.length > 0 && (
            <div className="focused-fit-note focused-fit-warning">
              <span aria-hidden>⚠️</span>{" "}
              {flaggedCitationIds.length} citation
              {flaggedCitationIds.length === 1 ? "" : "s"} need
              {flaggedCitationIds.length === 1 ? "s" : ""} checking — click the ⚠
              on a citation to open its source.{" "}
              <button
                type="button"
                className="link-button"
                onClick={() => (showFlagged ? setShowFlagged(false) : revealFlagged())}
              >
                {showFlagged ? "hide" : "show"}
              </button>
            </div>
          )}
          <AiAnswer
            text={state.reply}
            records={records}
            verification={state.verification}
            emphasizeFlagged={showFlagged}
            onNavigateToRecord={onOpenSource}
          />
        </div>
      )}
    </div>
  );
}

// One concept group's chip box: OR'd variants of a single concept, with
// add/remove chip and remove-group controls. Its own add-term input keeps
// the parent panel simple.
function ConceptGroup({
  terms,
  index,
  isLast,
  disabled,
  onAddChip,
  onRemoveChip,
  onRemoveGroup,
  chipCounts,
}: {
  terms: string[];
  index: number;
  isLast: boolean;
  disabled: boolean;
  onAddChip: (groupIdx: number, term: string) => void;
  onRemoveChip: (groupIdx: number, term: string) => void;
  onRemoveGroup: (groupIdx: number) => void;
  chipCounts: Record<string, number>;
}) {
  const [addText, setAddText] = useState("");
  const submitAdd = () => {
    if (!addText.trim()) return;
    onAddChip(index, addText);
    setAddText("");
  };
  return (
    <>
      <div className="focused-group">
        <div className="focused-group-head">
          <span className="focused-group-label">Concept {index + 1}</span>
          <span className="focused-group-op">any of (OR)</span>
          <button
            type="button"
            className="focused-group-remove"
            aria-label={`Remove concept ${index + 1}`}
            onClick={() => onRemoveGroup(index)}
            disabled={disabled}
          >
            Remove
          </button>
        </div>
        <div className="focused-chips">
          {terms.map((t) => (
            <span key={t} className="focused-chip">
              {t}
              {typeof chipCounts[t] === "number" && (
                <span
                  className="focused-chip-count"
                  title={`${chipCounts[t]} record${chipCounts[t] === 1 ? "" : "s"} contain this term — remove it if it's too broad`}
                >
                  {chipCounts[t]}
                </span>
              )}
              <button
                type="button"
                className="focused-chip-remove"
                aria-label={`Remove ${t}`}
                onClick={() => onRemoveChip(index, t)}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
          <input
            className="focused-chip-add"
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitAdd();
              }
            }}
            placeholder="+ add term"
            disabled={disabled}
          />
        </div>
      </div>
      {!isLast && <div className="focused-and">AND</div>}
    </>
  );
}

// Focused Answer search builder: the phrase box + the editable CONCEPT-GROUP
// chips (OR within a group, AND across groups). Teaches the flow inline. The
// matched-set funnel, tab strip, and ask-actions (FocusedFunnel /
// ConversationTabs / FocusedActions) live below — this component only builds
// the search.
function FocusedPanel({
  phrase,
  setPhrase,
  groups,
  newConcept,
  setNewConcept,
  preparing,
  focusedError,
  disabled,
  sending,
  onExpand,
  onAddConcept,
  onAddChip,
  onRemoveChip,
  onRemoveGroup,
  excluded,
  onAddBack,
  onRetry,
  message,
  chipCounts,
}: {
  phrase: string;
  setPhrase: (v: string) => void;
  groups: string[][];
  newConcept: string;
  setNewConcept: (v: string) => void;
  preparing: boolean;
  focusedError: string | null;
  disabled: boolean;
  sending: boolean;
  onExpand: () => void;
  onAddConcept: () => void;
  onAddChip: (groupIdx: number, term: string) => void;
  onRemoveChip: (groupIdx: number, term: string) => void;
  onRemoveGroup: (groupIdx: number) => void;
  excluded: Array<{ term: string; count: number }>;
  onAddBack: (term: string) => void;
  onRetry: () => void;
  message: string | null;
  chipCounts: Record<string, number>;
}) {
  const hasGroups = groups.length > 0;

  return (
    <div className="focused-panel">
      <div className="focused-explainer">
        <strong>Ask a question in plain English.</strong> The tool extracts what
        to search for, you confirm the terms, then it answers using{" "}
        <em>only</em> the matching records — so nothing gets summarised away.
        Question words (when, every, response…) aren't searched; edit the terms
        below to broaden or narrow.
      </div>

      <div className="focused-phrase-row">
        <textarea
          className="focused-phrase"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onExpand();
            }
          }}
          placeholder="e.g. every time I requested a mechanical inspection and the response, or if it went unanswered"
          rows={2}
          disabled={disabled || sending}
        />
        <Tooltip title="Search your records for these words (no AI, no usage charge)">
          <button
            type="button"
            className="btn-secondary"
            onClick={onExpand}
            disabled={disabled || sending || preparing || !phrase.trim()}
          >
            {preparing ? "Searching…" : "Find records"}
          </button>
        </Tooltip>
      </div>

      {focusedError && (
        <div className="focused-prepare-error" role="alert">
          <span>{focusedError}</span>
          <button
            type="button"
            className="btn-secondary"
            onClick={onRetry}
            disabled={disabled || sending || preparing}
          >
            {preparing ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}

      {message && !focusedError && (
        <div className="focused-pinbar-note" role="status">
          {message}
        </div>
      )}

      {hasGroups && (
        <div className="focused-terms">
          <div className="focused-terms-hint">
            These are the words being searched. Within a concept, a record
            matches if it contains <strong>any</strong> chip (OR); it must
            match <strong>every</strong> concept (AND). Add terms to broaden a
            concept, remove to narrow. Word variants match automatically (e.g.{" "}
            <em>inspection</em> also matches <em>inspected</em>). Add a second
            concept to require another thing (e.g. the insurer's name).
          </div>
          <div className="focused-groups">
            {groups.map((g, i) => (
              <ConceptGroup
                key={i}
                terms={g}
                index={i}
                isLast={i === groups.length - 1}
                disabled={disabled || sending || preparing}
                onAddChip={onAddChip}
                onRemoveChip={onRemoveChip}
                onRemoveGroup={onRemoveGroup}
                chipCounts={chipCounts}
              />
            ))}
          </div>
          <div className="focused-add-concept">
            <input
              className="focused-chip-add"
              value={newConcept}
              onChange={(e) => setNewConcept(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddConcept();
                }
              }}
              placeholder="+ Add concept (AND) — e.g. the insurer or adjuster"
              disabled={disabled || sending || preparing}
            />
            <Tooltip title="Require another concept — records must match this too">
              <button
                type="button"
                className="btn-ghost"
                onClick={onAddConcept}
                disabled={disabled || sending || preparing || !newConcept.trim()}
              >
                Add concept
              </button>
            </Tooltip>
          </div>
        </div>
      )}

      {excluded.length > 0 && (
        <div className="focused-excluded">
          <div className="focused-excluded-label">
            Not searched (question words) — click to add one back into your
            search:
          </div>
          <div className="focused-chips">
            {excluded.map((e) => (
              <button
                key={e.term}
                type="button"
                className="focused-chip focused-chip-excluded"
                onClick={() => onAddBack(e.term)}
                disabled={disabled || sending || preparing}
                title={`Add "${e.term}" to your search — ${e.count} record${e.count === 1 ? "" : "s"} contain it`}
              >
                {e.term}
                <span className="focused-chip-count">{e.count}</span>
                <span className="focused-chip-addback" aria-hidden>
                  +
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Truncate a volume title for a compact dropdown label. Volume
// titles can be long ("Lost Check and Unpaid Reimbursement …");
// ~26 chars keeps options legible without wrapping the control.
function truncateTitle(title: string, max = 26): string {
  return title.length > max ? `${title.slice(0, max - 1).trimEnd()}…` : title;
}

function scopeOptionLabel(v: CaseVolume): string {
  const isOpen = v.closed_at === null;
  const fallback = isOpen ? "Active" : "Closed";
  const base = (v.title && v.title.trim()) || fallback;
  return `Volume ${v.volume_number} · ${truncateTitle(base)}`;
}

// Scope picker shown at the top of Interrogate for multi-volume
// cases. "Entire case" (default) uses the normal volume-aware
// endpoint; a volume id drills into that single volume via the
// /volumes/:id/interrogate endpoint. Single-volume cases render
// nothing.
function ScopeSelector({
  volumes,
  scope,
  onScopeChange,
}: {
  volumes: CaseVolume[];
  scope: InterrogateScope;
  onScopeChange: (scope: InterrogateScope) => void;
}) {
  if (volumes.length <= 1) return null;
  const ordered = [...volumes].sort(
    (a, b) => a.volume_number - b.volume_number,
  );
  return (
    <div className="interrogate-scope">
      <label className="interrogate-scope-label" htmlFor="interrogate-scope">
        Ask about:
      </label>
      <select
        id="interrogate-scope"
        className="interrogate-scope-select"
        value={scope}
        onChange={(e) => onScopeChange(e.target.value as InterrogateScope)}
      >
        <option value="case">Entire case</option>
        {ordered.map((v) => (
          <option key={v.id} value={v.id}>
            {scopeOptionLabel(v)}
          </option>
        ))}
      </select>
    </div>
  );
}

// Read-only label summarising what the AI can currently see under
// the selected scope. Replaces the old interactive indicator — all
// navigation now lives in ScopeSelector.
function VolumeContextIndicator({
  volumes,
  drillBackVolumeId,
}: {
  volumes: CaseVolume[];
  drillBackVolumeId: string | null;
}) {
  if (volumes.length <= 1) return null;

  const current = volumes.find((v) => v.closed_at === null) ?? null;
  const closed = volumes.filter((v) => v.closed_at !== null);

  if (drillBackVolumeId) {
    const target = volumes.find((v) => v.id === drillBackVolumeId);
    if (!target) return null;
    const targetIsOpen = target.closed_at === null;
    // Drill-back into the OPEN volume = "only current volume, no
    // closed summaries" per the spec. Drill-back into a closed
    // volume = that volume's records verbatim + current-volume
    // summary.
    const detail = targetIsOpen
      ? `${target.record_count} records from Volume ${target.volume_number} (full detail, no closed-volume summaries)`
      : `${target.record_count} records from Volume ${target.volume_number}${
          target.title ? ` (${target.title})` : ""
        } (full detail)${
          current ? ` + Volume ${current.volume_number} summary` : ""
        }`;
    return (
      <div className="interrogate-context-bar">
        <span className="interrogate-context-label">AI context: {detail}</span>
      </div>
    );
  }

  if (!current) return null;
  const summaryCount = closed.length;
  const label =
    summaryCount === 1
      ? `Volume 1 summary`
      : `${summaryCount} closed volume summaries`;
  return (
    <div className="interrogate-context-bar">
      <span className="interrogate-context-label">
        AI context: {label} + {current.record_count} records from Volume{" "}
        {current.volume_number} (full detail)
      </span>
    </div>
  );
}


// System message shown in an empty conversation so the user
// immediately understands what the AI can see under the current
// scope. Replaces the previous "DrillBackSystemMessage" — now
// handles both drill-back and entire-case scopes uniformly.
function ScopeSystemMessage({
  scope,
  volumes,
}: {
  scope: InterrogateScope;
  volumes: CaseVolume[];
}) {
  if (scope === "case") {
    const current = volumes.find((v) => v.closed_at === null) ?? null;
    const closedCount = volumes.filter((v) => v.closed_at !== null).length;
    const currentPart = current
      ? `full detail on Volume ${current.volume_number} (current)`
      : "full detail on the current volume";
    const summaryPart =
      closedCount === 0
        ? ""
        : closedCount === 1
          ? " and a summary of the earlier closed volume"
          : ` and summaries of ${closedCount} closed volumes`;
    return (
      <div className="drill-back-system" role="status">
        You're asking about the entire case. The AI has {currentPart}
        {summaryPart}. Ask anything.
      </div>
    );
  }
  const volume = volumes.find((v) => v.id === scope) ?? null;
  if (!volume) return null;
  const a = formatMonthYear(volume.first_record_date);
  const b = formatMonthYear(volume.last_record_date);
  const dateRange = a && b ? `${a} – ${b}` : a || b || "this period";
  const title = volume.title
    ? `Volume ${volume.volume_number} (${volume.title})`
    : `Volume ${volume.volume_number}`;
  return (
    <div className="drill-back-system" role="status">
      You're now asking about {title} ({dateRange}, {volume.record_count}{" "}
      records). The AI has full access to every record in this volume.
      Ask anything.
    </div>
  );
}
